import { supabase } from '@/lib/supabase/client';
import { DetectionResult } from '../types';
import { saveDetectionResult } from '../db/saveResult';
import { 
  analyzeImageMetadata, 
  enhancedClientSideAnalysis, 
  fallbackImageAnalysis,
  fileToBase64 
} from './utils';

// Main function to analyze images for fraud indicators using both client-side and server-side analysis
export async function analyzeImage(imageFile: File | string): Promise<DetectionResult> {
  try {
    console.log("Starting image analysis for:", typeof imageFile === 'string' ? 'base64 image' : imageFile.name);
    
    // First, let's extract and analyze the image metadata on the client side
    const metadataAnalysis = await analyzeImageMetadata(typeof imageFile === 'string' ? new File([imageFile], 'image') : imageFile);
    console.log("Metadata analysis completed:", metadataAnalysis);

    // Try to use the Supabase edge function for deeper analysis
    try {
      // Convert the image file to base64 for the API request
      const base64Image = await fileToBase64(typeof imageFile === 'string' ? new File([imageFile], 'image') : imageFile);
      console.log("Image converted to base64");
      
      // Call the Supabase function with the base64 image and metadata results
      console.log("Calling Supabase analyze-image function");
      const { data, error } = await supabase.functions.invoke('analyze-image', { 
        body: { 
          imageData: base64Image.split(',')[1], // Remove the data:image/jpeg;base64, part
          fileName: typeof imageFile === 'string' ? 'base64 image' : imageFile.name,
          fileType: typeof imageFile === 'string' ? 'image/jpeg' : imageFile.type,
          fileSize: typeof imageFile === 'string' ? 0 : imageFile.size,
          metadataFlags: metadataAnalysis.flags,
          metadataScore: metadataAnalysis.score
        }
      });
      
      if (error) {
        console.warn("Error calling Supabase function:", error);
        throw error;
      }
      
      if (!data) {
        throw new Error("No data returned from analysis function");
      }
      
      console.log("Supabase function returned data:", data);
      
      // Save the detection result to the database if user is authenticated
      const { data: session } = await supabase.auth.getSession();
      if (session?.session?.user) {
        try {
          await saveDetectionResult(
            session.session.user.id, 
            'image_analysis', 
            {
              ...data,
              timestamp: new Date()
            }
          );
          console.log("Analysis result saved to database");
        } catch (saveError) {
          console.error("Error saving detection result:", saveError);
          // Continue with the analysis regardless of save error
        }
      }
      
      return {
        score: data.score || 0,
        confidence: data.confidence || 0.8,
        flags: data.flags || [],
        explanation: data.explanation || "Image analysis complete. The image was analyzed for potential manipulation or AI generation.",
        modelName: data.modelName || "Image Forensics Model",
        timestamp: new Date()
      };
    } catch (supabaseError) {
      console.warn("Supabase function error, using enhanced client-side analysis:", supabaseError);
      
      // If server-side analysis failed, enhance our client-side analysis
      const enhancedAnalysis = await enhancedClientSideAnalysis(typeof imageFile === 'string' ? new File([imageFile], 'image') : imageFile, metadataAnalysis);
      console.log("Enhanced client-side analysis completed:", enhancedAnalysis);
      
      return {
        ...enhancedAnalysis,
        flags: [...(metadataAnalysis.flags || []), ...(enhancedAnalysis.flags || [])],
        explanation: enhancedAnalysis.explanation || metadataAnalysis.explanation || "Limited analysis performed using client-side techniques.",
        confidence: Math.max(enhancedAnalysis.confidence || 0, metadataAnalysis.confidence || 0),
        score: Math.max(enhancedAnalysis.score || 0, metadataAnalysis.score || 0),
        modelName: "Client-side Image Analysis",
        timestamp: new Date()
      };
    }
  } catch (error) {
    console.error("Error in image analysis:", error);
    return fallbackImageAnalysis(typeof imageFile === 'string' ? new File([imageFile], 'image') : imageFile);
  }
}
